import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { PrenatalIntakeFormData, WORRY_OPTIONS, TOPICS_TO_LEARN } from "../PrenatalIntakeFormSchema";

interface NeedsAndConcernsSectionProps {
  form: UseFormReturn<PrenatalIntakeFormData>;
}

export function NeedsAndConcernsSection({ form }: NeedsAndConcernsSectionProps) {
  const selectedWorries = form.watch("worries") || [];

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold border-b pb-2">Member Needs & Concerns</h3>

      {/* Worries */}
      <FormField
        control={form.control}
        name="worries"
        render={() => (
          <FormItem>
            <FormLabel>Are you very worried about any of the following?</FormLabel>
            <FormDescription>Check all that apply</FormDescription>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {WORRY_OPTIONS.map((option) => (
                <FormField
                  key={option.value}
                  control={form.control}
                  name="worries"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(option.value)}
                          onCheckedChange={(checked) => {
                            const currentValue = field.value || [];
                            if (checked) {
                              field.onChange([...currentValue, option.value]);
                            } else {
                              field.onChange(currentValue.filter((v) => v !== option.value));
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">{option.label}</FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {selectedWorries.length > 0 && (
        <FormField
          control={form.control}
          name="primary_worry"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Which concern are you most worried about?</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Describe your primary concern..."
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Coping Strategies */}
      <FormField
        control={form.control}
        name="coping_strategies"
        render={({ field }) => (
          <FormItem>
            <FormLabel>How do you cope with problems?</FormLabel>
            <FormDescription>How have you overcome problems in the past?</FormDescription>
            <FormControl>
              <Textarea 
                placeholder="e.g., I pray, talk to family, exercise, etc." 
                className="min-h-[80px]"
                {...field} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Topics to Learn */}
      <FormField
        control={form.control}
        name="topics_to_learn"
        render={() => (
          <FormItem>
            <FormLabel>What topics would you like to learn more about?</FormLabel>
            <FormDescription>Check all that apply</FormDescription>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {TOPICS_TO_LEARN.map((topic) => (
                <FormField
                  key={topic.value}
                  control={form.control}
                  name="topics_to_learn"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(topic.value)}
                          onCheckedChange={(checked) => {
                            const currentValue = field.value || [];
                            if (checked) {
                              field.onChange([...currentValue, topic.value]);
                            } else {
                              field.onChange(currentValue.filter((v) => v !== topic.value));
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">{topic.label}</FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Additional Notes */}
      <FormField
        control={form.control}
        name="additional_notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Additional Information</FormLabel>
            <FormDescription>Anything else you'd like us to know?</FormDescription>
            <FormControl>
              <Textarea 
                placeholder="Share any additional information..." 
                className="min-h-[100px]"
                {...field} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
